import React, { useState, FormEvent, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { PasswordResetConfirmRequest, validatePasswordPolicy, PasswordPolicyViolationType } from '@df-gate-share/shared';
import { apiClient, getErrorMessage } from '../lib/api';
import MkdfLogo from '../components/MkdfLogo';
import DFFilesLogo from '../components/DFFilesLogo';

const POLICY_LABELS: Record<PasswordPolicyViolationType, string> = {
  minLength: '8文字以上',
  maxLength: '128文字以内',
  uppercase: '大文字(A-Z)',
  lowercase: '小文字(a-z)',
  digit: '数字(0-9)',
  symbol: '記号(!@#$%...)',
  sameAsCurrent: '現在のパスワードと異なる',
};

const POLICY_CHECKS: PasswordPolicyViolationType[] = [
  'minLength',
  'maxLength',
  'uppercase',
  'lowercase',
  'digit',
  'symbol',
];

const PasswordResetConfirm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Real-time password policy validation
  const policyResult = validatePasswordPolicy(newPassword);
  const satisfiedPolicies = new Set(
    POLICY_CHECKS.filter((p) => !policyResult.violations.includes(p))
  );

  // Password match check
  const passwordsMatch = newPassword === confirmPassword;
  const hasConfirmInput = confirmPassword.length > 0;
  const isFormValid =
    policyResult.valid && passwordsMatch && hasConfirmInput && !!token;

  // Auto-redirect to login after success
  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    const timer = setTimeout(() => {
      navigate('/login');
    }, 3000);
    return () => clearTimeout(timer);
  }, [isSuccess, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError('');
    setConfirmError('');

    if (!passwordsMatch) {
      setConfirmError('パスワードが一致しません');
      return;
    }

    if (!policyResult.valid) {
      return;
    }

    if (!token) {
      return;
    }

    setIsLoading(true);

    try {
      const requestBody: PasswordResetConfirmRequest = {
        token,
        newPassword,
      };

      await apiClient.post('/api/v1/auth/password-reset/confirm', requestBody);
      setIsSuccess(true);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // No token provided - show error immediately
  if (!token) {
    return (
      <div className="hero-gradient">
        <div className="hero-card">
          <div className="hero-card__header">
            <div className="hero-card__logo">
              <MkdfLogo height={28} />
              <span className="header__brand-divider" style={{ margin: '0 8px', opacity: 0.3 }}>|</span>
              <DFFilesLogo size={24} variant="light" />
            </div>
            <div className="hero-card__tagline">パスワード再設定</div>
          </div>
          <div className="hero-card__body">
            <div className="alert alert--error">
              リセットリンクが無効または期限切れです
            </div>
            <Link to="/password-reset" className="btn btn--secondary btn--block mt-4">
              パスワードリセットを再申請
            </Link>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Link to="/login">ログイン画面に戻る</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-gradient">
      <div className="hero-card">
        <div className="hero-card__header">
          <div className="hero-card__logo">
              <MkdfLogo height={28} />
              <span className="header__brand-divider" style={{ margin: '0 8px', opacity: 0.3 }}>|</span>
              <DFFilesLogo size={24} variant="light" />
            </div>
          <div className="hero-card__tagline">パスワード再設定</div>
        </div>

        <div className="hero-card__body">
          {isSuccess ? (
            <div>
              <div className="alert alert--success">
                パスワードが正常に変更されました。ログインしてください。
              </div>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', textAlign: 'center', marginTop: '0.5rem' }}>
                3秒後にログイン画面に移動します...
              </p>
            </div>
          ) : (
            <>
              {apiError && (
                <div>
                  <div className="alert alert--error">{apiError}</div>
                  <Link to="/password-reset" className="btn btn--secondary btn--block mt-4 mb-4">
                    パスワードリセットを再申請
                  </Link>
                </div>
              )}

              {!apiError && (
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="newPassword" className="form-label">新しいパスワード</label>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isLoading}
                      placeholder="新しいパスワードを入力"
                      autoComplete="new-password"
                      className="form-input"
                    />
                  </div>

                  {/* Password strength indicator */}
                  <div className="policy-list mb-4">
                    {POLICY_CHECKS.map((policy) => (
                      <div
                        key={policy}
                        data-testid="policy-item"
                        className={`policy-item ${satisfiedPolicies.has(policy) ? 'policy-item--satisfied' : ''}`}
                      >
                        <span className="policy-item__icon">
                          {satisfiedPolicies.has(policy) ? '✓' : '○'}
                        </span>
                        {POLICY_LABELS[policy]}
                      </div>
                    ))}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">新しいパスワード（確認）</label>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      placeholder="パスワードを再入力"
                      autoComplete="new-password"
                      className={`form-input ${hasConfirmInput && !passwordsMatch ? 'form-input--error' : ''}`}
                    />
                    {hasConfirmInput && !passwordsMatch && (
                      <p className="form-error">パスワードが一致しません</p>
                    )}
                    {confirmError && passwordsMatch && (
                      <p className="form-error">{confirmError}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !isFormValid}
                    className="btn btn--primary btn--lg btn--block mb-4"
                  >
                    {isLoading ? '変更中...' : 'パスワードを変更'}
                  </button>

                  <div style={{ textAlign: 'center' }}>
                    <Link to="/login">ログイン画面に戻る</Link>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;
